import React, { useState } from 'react';
import type { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/ToastContainer';
import FileUpload from '../components/FileUpload';
import OptionInput from '../components/OptionInput';
import AudioPlaybackSettings, { describeAudioPlayback } from '../components/AudioPlaybackSettings';

interface Quiz {
  title: string;
  description: string;
  category?: string;
  quiz_type?: 'quiz' | 'evaluation' | 'assessment' | 'survey';
  time_limit_minutes?: number; // Time limit in minutes
  require_email?: boolean; // Whether email is required for this quiz
  audio_max_plays?: number | null;
  audio_allow_pause?: boolean;
  audio_allow_seek?: boolean;
}

interface Question {
  text: string;
  question_type: string;
  order_num: number;
  id?: number;
  options?: Option[];
  correct_text_answer?: string; // For text questions: the correct answer
  requires_correct_answer?: boolean; // Whether this question requires a correct answer to be defined
  allow_multiple_answers?: boolean; // For multiple choice: whether multiple answers can be selected
  image_file?: string;
}

interface Option {
  text: string;
  is_correct: boolean;
  image_file?: File | null;
}

const CreateQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [quiz, setQuiz] = useState<Quiz>({
    title: '',
    description: '',
    category: '',
    quiz_type: 'quiz',
    time_limit_minutes: undefined,
    require_email: true,
    audio_max_plays: null,
    audio_allow_pause: true,
    audio_allow_seek: true
  });
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    text: '',
    question_type: 'multiple_choice',
    order_num: 1,
    requires_correct_answer: false,
    correct_text_answer: '',
    allow_multiple_answers: false
  });
  
  const [options, setOptions] = useState<Option[]>([
    { text: '', is_correct: true, image_file: null },
    { text: '', is_correct: false, image_file: null }
  ]);
  
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [questionImageFiles, setQuestionImageFiles] = useState<File[]>([]);
  const [showOptionImages, setShowOptionImages] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState<'basic' | 'questions' | 'complete'>('basic');
  const [createdQuizId, setCreatedQuizId] = useState<number | null>(null);

  // New states for multiple questions per audio file
  const [questionMode, setQuestionMode] = useState<'single' | 'multiple'>('single');
  const [groupAssetType, setGroupAssetType] = useState<'audio' | 'image'>('audio');
  const [bulkQuestions, setBulkQuestions] = useState<Question[]>([]);
  const [currentBulkQuestion, setCurrentBulkQuestion] = useState<Question>({
    text: '',
    question_type: 'multiple_choice',
    order_num: 1,
    requires_correct_answer: false,
    correct_text_answer: '',
    allow_multiple_answers: false
  });
  const [currentBulkOptions, setCurrentBulkOptions] = useState<Option[]>([
    { text: '', is_correct: true, image_file: null },
    { text: '', is_correct: false, image_file: null }
  ]);
  const [showBulkOptionImages, setShowBulkOptionImages] = useState(false);

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAudioFile(e.target.files[0]);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleQuestionImagesAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setQuestionImageFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeQuestionImageAt = (idx: number) => {
    setQuestionImageFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const moveQuestionImage = (idx: number, direction: -1 | 1) => {
    setQuestionImageFiles(prev => {
      const arr = [...prev];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      const temp = arr[idx];
      arr[idx] = arr[newIdx];
      arr[newIdx] = temp;
      return arr;
    });
  };


  const uploadAudio = async (): Promise<string | null> => {
    if (!audioFile) return null;
    
    try {
      setIsUploading(true);
      
      const formData = new FormData();
      formData.append('audio', audioFile);
      
      const response = await api.post('/api/audio/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setIsUploading(false);
      return response.data.audioFile;
    } catch (err) {
      console.error('Error uploading audio:', err);
      setIsUploading(false);
      showToast('Failed to upload audio file', 'error');
      return null;
    }
  };


  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('image', imageFile);
      const response = await api.post('/api/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIsUploading(false);
      return response.data.imageFile;
    } catch (err) {
      console.error('Error uploading image:', err);
      setIsUploading(false);
      showToast('Failed to upload image file', 'error');
      return null;
    }
  };

  const handleOptionChange = (index: number, field: 'text' | 'is_correct', value: string | boolean) => {
    setOptions(prev => 
      prev.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    );
  };

  const handleOptionImageChange = (index: number, file: File | null) => {
    setOptions(prev => 
      prev.map((option, i) => 
        i === index ? { ...option, image_file: file } : option
      )
    );
  };

  const addOption = () => {
    setOptions(prev => [...prev, { text: '', is_correct: false, image_file: null }]);
  };

  const removeOption = (index: number) => {
    if (options.length <= 2) {
      showToast('Multiple choice questions must have at least 2 options', 'warning');
      return;
    }
    
    setOptions(prev => prev.filter((_, i) => i !== index));
  };

  // New functions for bulk question management
  const handleBulkOptionChange = (index: number, field: 'text' | 'is_correct', value: string | boolean) => {
    setCurrentBulkOptions(prev => 
      prev.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    );
  };

  const handleBulkOptionImageChange = (index: number, file: File | null) => {
    setCurrentBulkOptions(prev => 
      prev.map((option, i) => 
        i === index ? { ...option, image_file: file } : option
      )
    );
  };

  const addBulkOption = () => {
    setCurrentBulkOptions(prev => [...prev, { text: '', is_correct: false, image_file: null }]);
  };

  const removeBulkOption = (index: number) => {
    if (currentBulkOptions.length <= 2) {
      showToast('Multiple choice questions must have at least 2 options', 'warning');
      return;
    }
    
    setCurrentBulkOptions(prev => prev.filter((_, i) => i !== index));
  };

  const addToBulkQuestions = () => {
    // Validate current bulk question
    if (!currentBulkQuestion.text.trim()) {
      showToast('Please enter a question text', 'error');
      return;
    }

    // Validate options for multiple choice
    if (currentBulkQuestion.question_type === 'multiple_choice') {
      // Check that each option has either text or an image
      const invalidOptions = currentBulkOptions.some(opt => !opt.text.trim() && !opt.image_file);
      if (invalidOptions) {
        showToast('Each option must have either text or an image', 'error');
        return;
      }

      const hasCorrectOption = currentBulkOptions.some(opt => opt.is_correct);
      if (!hasCorrectOption) {
        showToast('Please select at least one correct answer', 'error');
        return;
      }
    }

    // Validate text answer correct answer if required
    if (currentBulkQuestion.question_type === 'text' && currentBulkQuestion.requires_correct_answer) {
      if (!currentBulkQuestion.correct_text_answer?.trim()) {
        showToast('Please provide the correct answer for this text question', 'error');
        return;
      }
    }

    // Add question to bulk list
    const newQuestion = {
      ...currentBulkQuestion,
      options: currentBulkQuestion.question_type === 'multiple_choice' ? [...currentBulkOptions] : undefined,
      order_num: bulkQuestions.length + 1
    };

    setBulkQuestions(prev => [...prev, newQuestion]);

    // Reset form
    setCurrentBulkQuestion({
      text: '',
      question_type: 'multiple_choice',
      order_num: bulkQuestions.length + 2,
      requires_correct_answer: false,
      correct_text_answer: '',
      allow_multiple_answers: false
    });
    setCurrentBulkOptions([
      { text: '', is_correct: true },
      { text: '', is_correct: false }
    ]);

    showToast('Question added to group!', 'success');
  };

  const removeBulkQuestion = (index: number) => {
    setBulkQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const submitBulkQuestions = async () => {
    if (!createdQuizId) {
      showToast('Please create the quiz first', 'error');
      return;
    }

    if (bulkQuestions.length === 0) {
      showToast('Please add at least one question to the group', 'error');
      return;
    }

    if (groupAssetType === 'audio' && !audioFile) {
      showToast('Please upload an audio file for this question group', 'error');
      return;
    }
    if (groupAssetType === 'image' && !imageFile) {
      showToast('Please upload an image file for this question group', 'error');
      return;
    }

    try {
      setIsCreating(true);

      let response: AxiosResponse<{ questions: Question[] }>;
      if (groupAssetType === 'audio') {
        const audioFileName = await uploadAudio();
        if (!audioFileName) throw new Error('Failed to upload audio file');
        response = await api.post(
          `/api/quizzes/${createdQuizId}/questions-with-audio`,
          {
            audio_file: audioFileName,
            questions: bulkQuestions.map(q => ({
              text: q.text,
              question_type: q.question_type,
              options: q.options,
              correct_text_answer: q.question_type === 'text' ? q.correct_text_answer : undefined,
              requires_correct_answer: q.question_type === 'text' ? q.requires_correct_answer : false,
              allow_multiple_answers: q.question_type === 'multiple_choice' ? q.allow_multiple_answers : false
            }))
          }
        );
      } else {
        const imageFileName = await uploadImage();
        if (!imageFileName) throw new Error('Failed to upload image file');
        response = await api.post(
          `/api/quizzes/${createdQuizId}/questions-with-image`,
          {
            image_file: imageFileName,
            questions: bulkQuestions.map(q => ({
              text: q.text,
              question_type: q.question_type,
              options: q.options,
              correct_text_answer: q.question_type === 'text' ? q.correct_text_answer : undefined,
              requires_correct_answer: q.question_type === 'text' ? q.requires_correct_answer : false,
              allow_multiple_answers: q.question_type === 'multiple_choice' ? q.allow_multiple_answers : false
            }))
          }
        );
      }

      // Update local state
      setQuestions(prev => [...prev, ...response.data.questions]);

      // Reset bulk form
      setBulkQuestions([]);
      setCurrentBulkQuestion({
        text: '',
        question_type: 'multiple_choice',
        order_num: 1,
        requires_correct_answer: false,
        correct_text_answer: '',
        allow_multiple_answers: false
      });
      setCurrentBulkOptions([
        { text: '', is_correct: true, image_file: null },
        { text: '', is_correct: false, image_file: null }
      ]);
      setAudioFile(null);
      setImageFile(null);

      showToast(
        `Successfully added ${response.data.questions.length} questions with shared ${groupAssetType}!`,
        'success'
      );

    } catch (err: any) {
      console.error('Error submitting bulk questions:', err);
      showToast(`Failed to add questions with ${groupAssetType}`, 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const createQuiz = async () => {
    if (!quiz.title.trim()) {
      showToast('Please enter a quiz title', 'error');
      return;
    }

    try {
      setIsCreating(true);
      
      const response = await api.post('/api/quizzes', {
        title: quiz.title.trim(),
        description: quiz.description.trim(),
        category: quiz.category?.trim() || null,
        quiz_type: quiz.quiz_type || 'quiz',
        time_limit_minutes: quiz.time_limit_minutes || null,
        require_email: quiz.require_email,
        audio_max_plays: quiz.audio_max_plays || null,
        audio_allow_pause: quiz.audio_allow_pause,
        audio_allow_seek: quiz.audio_allow_seek
      });
      
      const newQuizId = response.data.id;
      setCreatedQuizId(newQuizId);
      setCurrentStep('questions');
      
      showToast('Quiz created successfully! Now add questions.', 'success');
      
    } catch (err: any) {
      console.error('Error creating quiz:', err);
      showToast('Failed to create quiz', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const addQuestion = async () => {
    if (!createdQuizId) {
      showToast('Please create the quiz first', 'error');
      return;
    }

    // Validate question
    if (!currentQuestion.text.trim()) {
      showToast('Please enter a question text', 'error');
      return;
    }

    // Validate options for multiple choice
    if (currentQuestion.question_type === 'multiple_choice') {
      // Check that each option has either text or an image
      const invalidOptions = options.some(opt => !opt.text.trim() && !opt.image_file);
      if (invalidOptions) {
        showToast('Each option must have either text or an image', 'error');
        return;
      }

      const hasCorrectOption = options.some(opt => opt.is_correct);
      if (!hasCorrectOption) {
        showToast('Please select at least one correct answer', 'error');
        return;
      }
    }

    // Validate text answer correct answer if required
    if (currentQuestion.question_type === 'text' && currentQuestion.requires_correct_answer) {
      if (!currentQuestion.correct_text_answer?.trim()) {
        showToast('Please provide the correct answer for this text question', 'error');
        return;
      }
    }

    try {
      setIsCreating(true);

      // Upload media if provided
      let audioFileName = null;
      let imageFileName = null;
      const imageFilesUploaded: string[] = [];
      if (audioFile) audioFileName = await uploadAudio();
      if (imageFile) imageFileName = await uploadImage();
      // Upload gallery images
      for (const f of questionImageFiles) {
        const form = new FormData();
        form.append('image', f);
        const up = await api.post('/api/images/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageFilesUploaded.push(up.data.imageFile);
      }

      // Create the question
      const questionData = {
        quiz_id: createdQuizId,
        text: currentQuestion.text,
        question_type: currentQuestion.question_type,
        audio_file: audioFileName,
        image_file: imageFileName || undefined,
        order_num: questions.length + 1,
        correct_text_answer: currentQuestion.question_type === 'text' ? currentQuestion.correct_text_answer : undefined,
        requires_correct_answer: currentQuestion.question_type === 'text' ? currentQuestion.requires_correct_answer : false,
        allow_multiple_answers: currentQuestion.question_type === 'multiple_choice' ? currentQuestion.allow_multiple_answers : false
      };

      const response = await api.post(
        `/api/quizzes/${createdQuizId}/questions`, 
        imageFilesUploaded.length > 0
          ? { ...questionData, image_files: imageFilesUploaded.map(fn => ({ image_file: fn })) }
          : questionData
      );

      const newQuestionId = response.data.id;

      // Add options for multiple choice questions
      if (currentQuestion.question_type === 'multiple_choice' && newQuestionId) {
        for (const option of options) {
          let optionImageFile = null;
          
          // Upload option image if present
          if (option.image_file) {
            const formData = new FormData();
            formData.append('image', option.image_file);
            const uploadResponse = await api.post('/api/images/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            optionImageFile = uploadResponse.data.imageFile;
          }
          
          await api.post(
            `/api/quizzes/questions/${newQuestionId}/options`,
            {
              text: option.text,
              is_correct: option.is_correct,
              image_file: optionImageFile
            }
          );
        }
      }

      // Update local state
      const newQuestion = { ...questionData, id: newQuestionId, options: [...options] };
      setQuestions(prev => [...prev, newQuestion]);

      // Reset form
      setCurrentQuestion({
        text: '',
        question_type: 'multiple_choice',
        order_num: questions.length + 2,
        requires_correct_answer: false,
        correct_text_answer: '',
        allow_multiple_answers: false
      });
      setOptions([
        { text: '', is_correct: true, image_file: null },
        { text: '', is_correct: false, image_file: null }
      ]);
      setAudioFile(null);
      setImageFile(null);
      setQuestionImageFiles([]);
      setShowOptionImages(false);

      showToast('Question added successfully!', 'success');

    } catch (err: any) {
      console.error('Error adding question:', err);
      showToast('Failed to add question', 'error');
      throw err; // Re-throw the error so finishQuiz can catch it
    } finally {
      setIsCreating(false);
    }
  };

  // Helper function to check if there's unsaved content
  const hasUnsavedContent = () => {
    return currentQuestion.text.trim() !== '' || 
      (currentQuestion.question_type === 'multiple_choice' && options.some(opt => opt.text.trim() !== '')) ||
      audioFile !== null || imageFile !== null;
  };

  // Helper function to check if current question can be auto-saved
  const canAutoSaveCurrentQuestion = () => {
    return currentQuestion.text.trim() !== '' && 
      (currentQuestion.question_type !== 'multiple_choice' || 
       (options.every(opt => opt.text.trim() !== '') && options.some(opt => opt.is_correct)));
  };

  const finishQuiz = async () => {
    // Check if there's a current question with content that hasn't been saved
    const hasUnsavedQuestion = hasUnsavedContent();
    
    if (hasUnsavedQuestion) {
      // Check if the current question is valid and can be saved automatically
      const canAutoSave = currentQuestion.text.trim() !== '' && 
        (currentQuestion.question_type !== 'multiple_choice' || 
         (options.every(opt => opt.text.trim() !== '') && options.some(opt => opt.is_correct)));
      
      if (canAutoSave) {
        // Automatically save the valid question
        try {
          await addQuestion();
          // If successful, proceed to finish
          setCurrentStep('complete');
          showToast('Question saved and quiz completed successfully!', 'success');
        } catch (error) {
          // If saving fails, don't finish the quiz
          showToast('Failed to save the current question. Please fix any issues and try again.', 'error');
          return;
        }
      } else {
        // Question has content but is incomplete/invalid - ask user what to do
        const shouldSaveCurrentQuestion = window.confirm(
          'You have an incomplete question. Would you like to save it before finishing the quiz? (Click Cancel to finish without saving this question)'
        );
        
        if (shouldSaveCurrentQuestion) {
          // Try to save the current question first
          try {
            await addQuestion();
            // If successful, proceed to finish
            setCurrentStep('complete');
            showToast('Quiz created successfully! Ready for use.', 'success');
          } catch (error) {
            // If saving fails, don't finish the quiz
            showToast('Please fix the current question before finishing the quiz.', 'error');
            return;
          }
        } else {
          // User chose not to save, proceed to finish
          if (questions.length === 0) {
            if (window.confirm('This quiz will have no questions. Are you sure you want to finish?')) {
              setCurrentStep('complete');
              showToast('Quiz created successfully! Ready for use.', 'success');
            }
          } else {
            setCurrentStep('complete');
            showToast('Quiz created successfully! Ready for use.', 'success');
          }
        }
      }
    } else {
      // No unsaved question, check if quiz has any questions at all
      if (questions.length === 0) {
        if (window.confirm('This quiz has no questions. Are you sure you want to finish?')) {
          setCurrentStep('complete');
          showToast('Quiz created successfully! Ready for use.', 'success');
        }
        return;
      }
      
      // Quiz has questions and no unsaved content, proceed to finish
      setCurrentStep('complete');
      showToast('Quiz created successfully! Ready for use.', 'success');
    }
  };

  const goToAdminDashboard = () => {
    navigate('/admin');
  };

  const editQuiz = () => {
    if (createdQuizId) {
      navigate(`/admin/quiz/${createdQuizId}/edit`);
    }
  };

  return (
    <div className="page create-quiz-page">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="page-title">Create New Quiz</h1>
          <button 
            className="btn btn-outline"
            onClick={() => navigate('/admin')}
          >
            Back to Dashboard
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator mb-3">
          <div className={`progress-step ${currentStep === 'basic' ? 'active' : (currentStep === 'questions' || currentStep === 'complete') ? 'completed' : 'inactive'}`}>
            <span className="progress-step-number">1</span>
            Basic Information
          </div>
          <div className={`progress-step ${currentStep === 'questions' ? 'active' : currentStep === 'complete' ? 'completed' : 'inactive'}`}>
            <span className="progress-step-number">2</span>
            Add Questions ({questions.length})
          </div>
          <div className={`progress-step ${currentStep === 'complete' ? 'active' : 'inactive'}`}>
            <span className="progress-step-number">3</span>
            Complete
          </div>
        </div>

        {/* Step 1: Basic Quiz Information */}
        {currentStep === 'basic' && (
          <div className="card cq-card" >
            <div className="card-header">
              <h2>Quiz Information</h2>
            </div>
            
            <div className="form-group">
              <label htmlFor="title">Quiz Title *</label>
              <input
                type="text"
                id="title"
                className="form-control"
                value={quiz.title}
                onChange={(e) => setQuiz(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter quiz title"
                maxLength={100}
              />
              <small className="form-text">{quiz.title.length}/100 characters</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="form-control"
                value={quiz.description}
                onChange={(e) => setQuiz(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                placeholder="Enter quiz description (optional)"
                maxLength={500}
              />
              <small className="form-text">{quiz.description.length}/500 characters</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <input
                type="text"
                id="category"
                className="form-control"
                value={quiz.category || ''}
                onChange={(e) => setQuiz(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., MTA, Centerlight, ConEd, etc. (optional)"
                maxLength={100}
              />
              <small className="form-text">Categorize your quiz for better organization</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="quiz_type">Quiz Type *</label>
              <select
                id="quiz_type"
                className="form-control"
                value={quiz.quiz_type || 'quiz'}
                onChange={(e) => setQuiz(prev => ({ ...prev, quiz_type: e.target.value as 'quiz' | 'evaluation' | 'assessment' | 'survey' }))}
              >
                <option value="quiz">Quiz - Regular quiz with scoring</option>
                <option value="evaluation">Evaluation Form - Trainer evaluation of trainees</option>
                <option value="assessment">Assessment - Formal assessment</option>
                <option value="survey">Survey - Feedback collection</option>
              </select>
              <small className="form-text">
                Choose the type of form. Evaluation forms are for trainers to evaluate trainees and don't have scoring.
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="time_limit">Time Limit (Optional)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  id="time_limit"
                  className="form-control"
                  value={quiz.time_limit_minutes || ''}
                  onChange={(e) => setQuiz(prev => ({ 
                    ...prev, 
                    time_limit_minutes: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  placeholder="Enter time limit"
                  min="1"
                  max="480"
                  style={{ maxWidth: '150px' }}
                />
                <span>minutes</span>
              </div>
              <small className="form-text">
                Set a time limit for this quiz (1-480 minutes). Leave empty for no time limit.
                {quiz.time_limit_minutes && (
                  <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                    {' '}Current: {quiz.time_limit_minutes} minute{quiz.time_limit_minutes !== 1 ? 's' : ''}
                  </span>
                )}
              </small>
            </div>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={quiz.require_email || false}
                  onChange={(e) => setQuiz(prev => ({ ...prev, require_email: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                Require email address from participants
              </label>
              <small className="form-text">
                {quiz.require_email 
                  ? 'Participants must provide an email address to submit this quiz' 
                  : 'Participants can submit this quiz without providing an email address'
                }
              </small>
            </div>

            <AudioPlaybackSettings
              value={quiz}
              onChange={(changes) => setQuiz(prev => ({ ...prev, ...changes }))}
            />
            
            <div className="d-flex gap-2 justify-content-center mt-3">
              <button
                className="btn btn-primary btn-large"
                onClick={createQuiz}
                disabled={isCreating || !quiz.title.trim()}
              >
                {isCreating ? 'Creating Quiz...' : 'Next step'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Add Questions */}
        {currentStep === 'questions' && (
          <>
            {/* Quiz Summary */}
            <div className="card">
              <div className="card-header">
                <h2>Quiz: {quiz.title}</h2>
                <span className="status-badge">
                  {questions.length} Question{questions.length !== 1 ? 's' : ''} Added
                </span>
              </div>
              <p><strong>Description:</strong> {quiz.description || 'No description'}</p>
              <p><strong>Time Limit:</strong> {
                quiz.time_limit_minutes 
                  ? `${quiz.time_limit_minutes} minute${quiz.time_limit_minutes !== 1 ? 's' : ''}` 
                  : 'No time limit'
              }</p>
            </div>

            {/* Existing Questions */}
            {questions.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h2>Questions Added</h2>
                </div>
                <div className="question-list">
                  {questions.map((question, index) => (
                    <div className="question-item" key={index}>
                      <div className="question-header">
                        <span className="question-number">Q{index + 1}</span>
                        <span className="question-type-badge">{question.question_type}</span>
                      </div>
                      <div className="question-text">
                        <strong>{question.text}</strong>
                      </div>
                      {question.question_type === 'multiple_choice' && question.options && (
                        <>
                          {question.allow_multiple_answers && (
                            <div className="badge badge-info" style={{ marginBottom: '8px' }}>
                              Multiple Answers ({question.options.filter(o => o.is_correct).length} correct)
                            </div>
                          )}
                          <div className="option-list">
                            {question.options.map((option, optIndex) => (
                              <div 
                                className={`option-item ${option.is_correct ? 'correct-option' : ''}`} 
                                key={optIndex}
                              >
                                {option.is_correct ? '✅' : '○'} {option.text}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {question.question_type === 'text' && (
                        <div className="text-answer-info">
                          {question.requires_correct_answer ? (
                            <div className="alert alert-info" style={{padding: '8px', margin: '8px 0', fontSize: '0.9em'}}>
                              Has correct answer: <em>"{question.correct_text_answer}"</em>
                            </div>
                          ) : (
                            <div className="alert alert-secondary" style={{padding: '8px', margin: '8px 0', fontSize: '0.9em'}}>
                              Open-ended (no specific correct answer)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Question Mode Selection */}
            <div className="card">
              <div className="card-header">
                <h2>Question Creation Mode</h2>
              </div>
              
              <div className="form-group">
                <label>Choose how you want to add questions:</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      value="single"
                      checked={questionMode === 'single'}
                      onChange={(e) => setQuestionMode(e.target.value as 'single' | 'multiple')}
                    />
                    <span className="radio-label">
                      <strong>Single Question</strong> - Add one question at a time (traditional method)
                    </span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      value="multiple"
                      checked={questionMode === 'multiple'}
                      onChange={(e) => setQuestionMode(e.target.value as 'single' | 'multiple')}
                    />
                    <span className="radio-label">
                      <strong>Multiple Questions per Media</strong> - Add several questions that share one audio or image
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Single Question Mode */}
            {questionMode === 'single' && (
              <div className="card">
                <div className="card-header">
                  <h2>Add Question #{questions.length + 1}</h2>
                </div>
              
              <div className="form-group">
                <label htmlFor="text">Question Text *</label>
                <textarea
                  id="text"
                  className="form-control"
                  value={currentQuestion.text}
                  onChange={(e) => setCurrentQuestion(prev => ({ ...prev, text: e.target.value }))}
                  rows={3}
                  placeholder="Enter your question"
                  maxLength={500}
                />
                <small className="form-text">{currentQuestion.text.length}/500 characters</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="question_type">Question Type</label>
                <select
                  id="question_type"
                  className="form-control"
                  value={currentQuestion.question_type}
                  onChange={(e) => setCurrentQuestion(prev => ({ 
                    ...prev, 
                    question_type: e.target.value as 'multiple_choice' | 'text' | 'audio_response'
                  }))}
                >
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="text">Text Answer</option>
                  <option value="audio_response">Audio Response</option>
                </select>
              </div>
              
              <FileUpload
                label="Audio File (Optional)"
                accept="audio/*"
                file={audioFile}
                onFileChange={setAudioFile}
                placeholder="Upload an audio file that goes with this question"
              />

              <FileUpload
                label="Image (Optional)"
                accept="image/*"
                file={imageFile}
                onFileChange={setImageFile}
                placeholder="Upload an image that goes with this question"
              />

              {/* Question Images Gallery */}
              <div className="form-group">
                <label>Question Images (Gallery, Optional)</label>
                <input type="file" accept="image/*" multiple onChange={handleQuestionImagesAdd} className="form-control" />
                {questionImageFiles.length > 0 && (
                  <div className="gallery" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {questionImageFiles.map((file, idx) => (
                      <div key={idx} style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: 8 }}>
                        <img src={URL.createObjectURL(file)} alt={`qimg-${idx}`} style={{ maxHeight: 100, display: 'block', borderRadius: 6 }} />
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button type="button" className="btn btn-small" onClick={() => moveQuestionImage(idx, -1)} disabled={idx===0}>↑</button>
                          <button type="button" className="btn btn-small" onClick={() => moveQuestionImage(idx, 1)} disabled={idx===questionImageFiles.length-1}>↓</button>
                          <button type="button" className="btn btn-danger btn-small" onClick={() => removeQuestionImageAt(idx)}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-text">Add, remove, and reorder multiple images for this question.</small>
              </div>
              
              {/* Text Answer Settings */}
              {currentQuestion.question_type === 'text' && (
                <div className="text-answer-settings">
                  <h3>Text Answer Settings</h3>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={currentQuestion.requires_correct_answer || false}
                        onChange={(e) => setCurrentQuestion(prev => ({
                          ...prev,
                          requires_correct_answer: e.target.checked,
                          correct_text_answer: e.target.checked ? prev.correct_text_answer : ''
                        }))}
                        style={{ marginRight: '8px' }}
                      />
                      This question has a specific correct answer
                    </label>
                    <small className="form-text">
                      Check this if you want to define what the correct answer should be for grading purposes.
                    </small>
                  </div>
                  
                  {currentQuestion.requires_correct_answer && (
                    <div className="form-group">
                      <label htmlFor="correct_text_answer">Correct Answer *</label>
                      <textarea
                        id="correct_text_answer"
                        className="form-control"
                        value={currentQuestion.correct_text_answer || ''}
                        onChange={(e) => setCurrentQuestion(prev => ({
                          ...prev,
                          correct_text_answer: e.target.value
                        }))}
                        rows={2}
                        placeholder="Enter the correct answer for this question"
                        maxLength={500}
                      />
                      <small className="form-text">
                        This answer will be used to automatically grade text responses. 
                        Comparison is case-insensitive and ignores punctuation.
                        {currentQuestion.correct_text_answer && (
                          <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                            {' '}Length: {currentQuestion.correct_text_answer.length}/500
                          </span>
                        )}
                      </small>
                    </div>
                  )}
                </div>
              )}
              
              {currentQuestion.question_type === 'multiple_choice' && (
                <div className="options-section">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{ margin: 0 }}>Options</h3>
                    <button
                      type="button"
                      className="btn btn-small btn-outline"
                      onClick={() => setShowOptionImages(!showOptionImages)}
                      title="Add images to options"
                      style={{
                        background: showOptionImages ? 'rgba(33, 150, 243, 0.2)' : undefined,
                        borderColor: showOptionImages ? 'rgba(33, 150, 243, 0.5)' : undefined,
                        color: showOptionImages ? '#1976d2' : undefined
                      }}
                    >
                      🖼️ {showOptionImages ? 'Hide' : 'Add'} Images
                    </button>
                  </div>
                  
                  {/* Toggle for multiple answers */}
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={currentQuestion.allow_multiple_answers || false}
                        onChange={(e) => {
                          setCurrentQuestion(prev => ({
                            ...prev,
                            allow_multiple_answers: e.target.checked
                          }));
                          // Reset options to have at least one correct answer
                          if (e.target.checked && !options.some(opt => opt.is_correct)) {
                            setOptions(prev => prev.map((opt, i) => ({
                              ...opt,
                              is_correct: i === 0
                            })));
                          }
                        }}
                      />
                      <span>Allow multiple correct answers</span>
                    </label>
                    <small className="form-text">
                      {currentQuestion.allow_multiple_answers 
                        ? 'Participants can select multiple answers. Partial credit will be given based on correct selections.'
                        : 'Participants can only select one answer.'}
                    </small>
                  </div>
                  
                  {options.map((option, index) => (
                    <OptionInput
                      key={index}
                      option={option}
                      index={index}
                      allowMultiple={currentQuestion.allow_multiple_answers || false}
                      showImageUploads={showOptionImages}
                      onOptionChange={handleOptionChange}
                      onImageChange={handleOptionImageChange}
                      onRemove={removeOption}
                      canRemove={options.length > 2}
                    />
                  ))}
                  
                  {currentQuestion.allow_multiple_answers && (
                    <div className="alert alert-info" style={{ marginTop: '10px', marginBottom: '10px' }}>
                      <strong>ℹ️ Multiple Answers Mode:</strong> Select all correct options. Participants will receive partial credit based on their selections.
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={addOption}
                    className="btn btn-outline"
                  >
                    Add Option
                  </button>
                </div>
              )}
              
              <div className="d-flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={addQuestion}
                  className="btn btn-primary"
                  disabled={isCreating || isUploading || !currentQuestion.text.trim()}
                >
                  {isCreating || isUploading ? (isUploading ? 'Uploading Audio...' : 'Adding Question...') : 'Add Question'}
                </button>
                <button
                  type="button"
                  onClick={finishQuiz}
                  className="btn btn-success"
                  disabled={isCreating}
                  title={
                    hasUnsavedContent() 
                      ? canAutoSaveCurrentQuestion() 
                        ? 'Will automatically save the current question and finish the quiz'
                        : 'Will ask if you want to save the incomplete question'
                      : 'Complete the quiz'
                  }
                >
                  {
                    hasUnsavedContent() && !canAutoSaveCurrentQuestion()
                      ? 'Save & Finish Quiz' 
                      : 'Finish Quiz'
                  }
                </button>
              </div>
              </div>
            )}

            {/* Multiple Questions per Media Mode */}
            {questionMode === 'multiple' && (
              <>
                {/* Media Upload Section */}
                <div className="card">
                  <div className="card-header">
                    <h2>📎 Upload Shared Media</h2>
                  </div>
                  
                  <div className="form-group">
                    <label>Use shared:</label>
                    <div className="radio-group">
                      <label className="radio-option">
                        <input type="radio" value="audio" checked={groupAssetType==='audio'} onChange={()=>setGroupAssetType('audio')} />
                        <span className="radio-label">Audio</span>
                      </label>
                      <label className="radio-option">
                        <input type="radio" value="image" checked={groupAssetType==='image'} onChange={()=>setGroupAssetType('image')} />
                        <span className="radio-label">Image</span>
                      </label>
                    </div>
                  </div>

                  {groupAssetType === 'audio' ? (
                    <FileUpload
                      label="Audio File *"
                      accept="audio/*"
                      file={audioFile}
                      onFileChange={setAudioFile}
                      placeholder="Upload one audio file that will be shared by multiple questions"
                    />
                  ) : (
                    <FileUpload
                      label="Image File *"
                      accept="image/*"
                      file={imageFile}
                      onFileChange={setImageFile}
                      placeholder="Upload one image that will be shared by multiple questions"
                    />
                  )}
                </div>

                {/* Questions in this group */}
                {bulkQuestions.length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <h2>Questions for this {groupAssetType === 'audio' ? 'Audio' : 'Image'} ({bulkQuestions.length})</h2>
                    </div>
                    <div className="question-list">
                      {bulkQuestions.map((question, index) => (
                        <div className="question-item" key={index}>
                          <div className="question-header">
                            <span className="question-number">Q{index + 1}</span>
                            <span className="question-type-badge">{question.question_type}</span>
                            <button
                              type="button"
                              onClick={() => removeBulkQuestion(index)}
                              className="btn btn-danger btn-small"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="question-text">
                            <strong>{question.text}</strong>
                          </div>
                          {question.question_type === 'multiple_choice' && question.options && (
                            <>
                              {question.allow_multiple_answers && (
                                <div className="badge badge-info" style={{ marginBottom: '8px' }}>
                                  Multiple Answers ({question.options.filter(o => o.is_correct).length} correct)
                                </div>
                              )}
                              <div className="option-list">
                                {question.options.map((option, optIndex) => (
                                  <div 
                                    className={`option-item ${option.is_correct ? 'correct-option' : ''}`} 
                                    key={optIndex}
                                  >
                                    {option.is_correct ? '✅' : '○'} {option.text}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {question.question_type === 'text' && (
                            <div className="text-answer-info">
                              {question.requires_correct_answer ? (
                                <div className="alert alert-info" style={{padding: '8px', margin: '8px 0', fontSize: '0.9em'}}>
                                  Has correct answer: <em>"{question.correct_text_answer}"</em>
                                </div>
                              ) : (
                                <div className="alert alert-secondary" style={{padding: '8px', margin: '8px 0', fontSize: '0.9em'}}>
                                  Open-ended (no specific correct answer)
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Question to Group Form */}
                <div className="card">
                  <div className="card-header">
                    <h2>Add Question to Group</h2>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bulk-text">Question Text *</label>
                    <textarea
                      id="bulk-text"
                      className="form-control"
                      value={currentBulkQuestion.text}
                      onChange={(e) => setCurrentBulkQuestion(prev => ({ ...prev, text: e.target.value }))}
                      rows={3}
                      placeholder="Enter your question"
                      maxLength={500}
                    />
                    <small className="form-text">{currentBulkQuestion.text.length}/500 characters</small>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bulk-question-type">Question Type</label>
                    <select
                      id="bulk-question-type"
                      className="form-control"
                      value={currentBulkQuestion.question_type}
                      onChange={(e) => setCurrentBulkQuestion(prev => ({ 
                        ...prev, 
                        question_type: e.target.value as 'multiple_choice' | 'text' | 'audio_response'
                      }))}
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="text">Text Answer</option>
                      <option value="audio_response">Audio Response</option>
                    </select>
                  </div>
                  
                  {/* Bulk Text Answer Settings */}
                  {currentBulkQuestion.question_type === 'text' && (
                    <div className="text-answer-settings">
                      <h3>Text Answer Settings</h3>
                      
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={currentBulkQuestion.requires_correct_answer || false}
                            onChange={(e) => setCurrentBulkQuestion(prev => ({
                              ...prev,
                              requires_correct_answer: e.target.checked,
                              correct_text_answer: e.target.checked ? prev.correct_text_answer : ''
                            }))}
                            style={{ marginRight: '8px' }}
                          />
                          This question has a specific correct answer
                        </label>
                        <small className="form-text">
                          Check this if you want to define what the correct answer should be for grading purposes.
                        </small>
                      </div>
                      
                      {currentBulkQuestion.requires_correct_answer && (
                        <div className="form-group">
                          <label htmlFor="bulk_correct_text_answer">Correct Answer *</label>
                          <textarea
                            id="bulk_correct_text_answer"
                            className="form-control"
                            value={currentBulkQuestion.correct_text_answer || ''}
                            onChange={(e) => setCurrentBulkQuestion(prev => ({
                              ...prev,
                              correct_text_answer: e.target.value
                            }))}
                            rows={2}
                            placeholder="Enter the correct answer for this question"
                            maxLength={500}
                          />
                          <small className="form-text">
                            This answer will be used to automatically grade text responses. 
                            Comparison is case-insensitive and ignores punctuation.
                            {currentBulkQuestion.correct_text_answer && (
                              <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                                {' '}Length: {currentBulkQuestion.correct_text_answer.length}/500
                              </span>
                            )}
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {currentBulkQuestion.question_type === 'multiple_choice' && (
                    <div className="options-section">
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{ margin: 0 }}>Options</h3>
                        <button
                          type="button"
                          className="btn btn-small btn-outline"
                          onClick={() => setShowBulkOptionImages(!showBulkOptionImages)}
                          title="Add images to options"
                          style={{
                            background: showBulkOptionImages ? 'rgba(33, 150, 243, 0.2)' : undefined,
                            borderColor: showBulkOptionImages ? 'rgba(33, 150, 243, 0.5)' : undefined,
                            color: showBulkOptionImages ? '#1976d2' : undefined
                          }}
                        >
                          🖼️ {showBulkOptionImages ? 'Hide' : 'Add'} Images
                        </button>
                      </div>
                      
                      {/* Toggle for multiple answers */}
                      <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            checked={currentBulkQuestion.allow_multiple_answers || false}
                            onChange={(e) => {
                              setCurrentBulkQuestion(prev => ({
                                ...prev,
                                allow_multiple_answers: e.target.checked
                              }));
                              // Reset options to have at least one correct answer
                              if (e.target.checked && !currentBulkOptions.some(opt => opt.is_correct)) {
                                setCurrentBulkOptions(prev => prev.map((opt, i) => ({
                                  ...opt,
                                  is_correct: i === 0
                                })));
                              }
                            }}
                          />
                          <span>Allow multiple correct answers</span>
                        </label>
                        <small className="form-text">
                          {currentBulkQuestion.allow_multiple_answers 
                            ? 'Participants can select multiple answers. Partial credit will be given based on correct selections.'
                            : 'Participants can only select one answer.'}
                        </small>
                      </div>
                      
                      {currentBulkOptions.map((option, index) => (
                        <OptionInput
                          key={index}
                          option={option}
                          index={index}
                          allowMultiple={currentBulkQuestion.allow_multiple_answers || false}
                          showImageUploads={showBulkOptionImages}
                          onOptionChange={handleBulkOptionChange}
                          onImageChange={handleBulkOptionImageChange}
                          onRemove={removeBulkOption}
                          canRemove={currentBulkOptions.length > 2}
                        />
                      ))}
                      
                      {currentBulkQuestion.allow_multiple_answers && (
                        <div className="alert alert-info" style={{ marginTop: '10px', marginBottom: '10px' }}>
                          <strong>ℹ️ Multiple Answers Mode:</strong> Select all correct options. Participants will receive partial credit based on their selections.
                        </div>
                      )}
                      
                      <button
                        type="button"
                        onClick={addBulkOption}
                        className="btn btn-outline"
                      >
                        Add Option
                      </button>
                    </div>
                  )}
                  
                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={addToBulkQuestions}
                      className="btn btn-primary"
                      disabled={!currentBulkQuestion.text.trim()}
                    >
                      Add to Group
                    </button>
                    
                    {bulkQuestions.length > 0 && (
                      <button
                        type="button"
                        onClick={submitBulkQuestions}
                        className="btn btn-success"
                        disabled={isCreating || (groupAssetType === 'audio' ? !audioFile : !imageFile)}
                      >
                        {isCreating ? 'Submitting Questions...' : `Submit ${bulkQuestions.length} Questions with ${groupAssetType === 'audio' ? 'Audio' : 'Image'}`}
                      </button>
                    )}
                    
                    <button
                      type="button"
                      onClick={finishQuiz}
                      className="btn btn-outline"
                      disabled={isCreating}
                    >
                      Finish Quiz
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Step 3: Complete */}
        {currentStep === 'complete' && (
          <div className="card">
            <div className="card-header">
              <h2>Quiz Created Successfully!</h2>
            </div>
            
            <div className="completion-status">
              <div className="alert alert-success">
                <strong>Quiz Ready!</strong> Your quiz "{quiz.title}" has been created with {questions.length} question{questions.length !== 1 ? 's' : ''}.
              </div>
              
              <div className="quiz-summary">
                <h3>Quiz Summary</h3>
                <ul>
                  <li><strong>Title:</strong> {quiz.title}</li>
                  <li><strong>Description:</strong> {quiz.description || 'No description'}</li>
                  <li><strong>Time Limit:</strong> {
                    quiz.time_limit_minutes 
                      ? `${quiz.time_limit_minutes} minute${quiz.time_limit_minutes !== 1 ? 's' : ''}` 
                      : 'No time limit'
                  }</li>
                  <li><strong>Audio Playback:</strong> {describeAudioPlayback(quiz)}</li>
                  <li><strong>Questions:</strong> {questions.length}</li>
                  <li><strong>Question Types:</strong> 
                    {Array.from(new Set(questions.map(q => q.question_type))).join(', ')}
                  </li>
                </ul>
              </div>
              
              <div className="d-flex gap-2 justify-content-center mt-3">
                <button 
                  className="btn btn-primary btn-large"
                  onClick={goToAdminDashboard}
                >
                  Go to Dashboard
                </button>
                <button 
                  className="btn btn-outline btn-large"
                  onClick={editQuiz}
                  disabled={!createdQuizId}
                >
                  Edit Quiz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateQuizPage; 